import { useState, useEffect } from 'react'
import { format, startOfWeek, endOfWeek } from 'date-fns'
import { getLessons, getInstructors } from '../../lib/supabase'
import { Download, TrendingUp, Lock } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'

export function AdminPayroll() {
  const { profile } = useAuth()

  if (profile?.plan_tier === 'starter') return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '48px 24px' }}>
      <div style={{ textAlign: 'center', maxWidth: 400 }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(232,97,74,0.08)', border: '1px solid rgba(232,97,74,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <Lock size={26} color="var(--coral)" />
        </div>
        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 24, color: '#0B1E3D', marginBottom: 10 }}>
          Payroll is a Pro Feature
        </h2>
        <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7, marginBottom: 28 }}>
          Upgrade to WaveSync Pro to unlock auto-calculated payroll, instructor pay tracking, and CSV export.
        </p>
        <a
          href="mailto:hello@wavesync.app?subject=Upgrade to Pro"
          style={{ display: 'inline-block', padding: '13px 32px', background: '#E8614A', color: 'white', borderRadius: 12, fontWeight: 700, fontSize: 14, textDecoration: 'none', fontFamily: 'DM Sans, sans-serif', boxShadow: '0 4px 14px rgba(232,97,74,0.3)' }}
        >
          Upgrade to Pro
        </a>
      </div>
    </div>
  )
  const [instructors, setInstructors] = useState([])
  const [lessons, setLessons] = useState([])
  const [loading, setLoading] = useState(true)
  const ws = format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
  const we = format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')

  useEffect(() => {
    Promise.all([getInstructors(), getLessons({ week_start: ws, week_end: we })]).then(
      ([{ data: ins }, { data: ls }]) => { setInstructors(ins || []); setLessons(ls || []); setLoading(false) }
    )
  }, [])

  const exportCSV = () => {
    const rows = instructors.map(inst => {
      const instLessons = lessons.filter(l => l.instructor_id === inst.id)
      const hours = instLessons.reduce((a, l) => {
        if (!l.start_time || !l.end_time) return a
        const [sh, sm] = l.start_time.split(':').map(Number)
        const [eh, em] = l.end_time.split(':').map(Number)
        return a + ((eh * 60 + em - sh * 60 - sm) / 60)
      }, 0)
      const pay = hours * (inst.hourly_rate || 25)
      return `${inst.profile?.full_name},${instLessons.length},${hours.toFixed(1)},${pay.toFixed(2)}`
    })
    const csv = ['Name,Lessons,Hours,Pay', ...rows].join('\n')
    const a = document.createElement('a'); a.href = 'data:text/csv,' + encodeURIComponent(csv)
    a.download = `wavesync-payroll-${ws}.csv`; a.click()
  }

  const total = instructors.reduce((acc, inst) => {
    const instLessons = lessons.filter(l => l.instructor_id === inst.id)
    const hours = instLessons.reduce((a, l) => {
      if (!l.start_time || !l.end_time) return a
      const [sh, sm] = l.start_time.split(':').map(Number)
      const [eh, em] = l.end_time.split(':').map(Number)
      return a + ((eh * 60 + em - sh * 60 - sm) / 60)
    }, 0)
    return acc + hours * (inst.hourly_rate || 25)
  }, 0)

  return (
    <div>
      <div className="stats-grid mb-6">
        {[
          { label: 'Total Payroll', value: `$${total.toFixed(0)}`, sub: 'This week' },
          { label: 'Instructors', value: instructors.length, sub: 'Active' },
          { label: 'Total Lessons', value: lessons.filter(l => l.instructor_id).length, sub: 'Staffed this week' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <h3 style={{ fontWeight: 600 }}>Weekly Payroll — {ws} to {we}</h3>
          <button className="btn btn-ghost btn-sm" onClick={exportCSV}>
            <Download size={14} /> Export CSV
          </button>
        </div>
        <div>
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 100px',
            padding: '0.625rem 1.5rem', background: 'rgba(11,30,61,0.03)',
            borderBottom: '1px solid rgba(11,30,61,0.07)',
            fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-light)',
            textTransform: 'uppercase', letterSpacing: '0.06em', gap: '1rem'
          }}>
            <div>Instructor</div><div>Lessons</div><div>Hours</div><div>Rate</div><div style={{ textAlign: 'right' }}>Pay</div>
          </div>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : instructors.map(inst => {
            const instLessons = lessons.filter(l => l.instructor_id === inst.id)
            const hours = instLessons.reduce((a, l) => {
              if (!l.start_time || !l.end_time) return a
              const [sh, sm] = l.start_time.split(':').map(Number)
              const [eh, em] = l.end_time.split(':').map(Number)
              return a + ((eh * 60 + em - sh * 60 - sm) / 60)
            }, 0)
            const pay = hours * (inst.hourly_rate || 25)
            const initials = (inst.profile?.full_name || '').split(' ').map(n => n[0]).join('').slice(0, 2)
            return (
              <div key={inst.id} style={{
                display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 100px',
                padding: '0.875rem 1.5rem', borderBottom: '1px solid rgba(11,30,61,0.05)',
                alignItems: 'center', gap: '1rem',
                transition: 'background 0.15s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(11,30,61,0.02)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="avatar" style={{ width: 34, height: 34, background: 'var(--ocean)', fontSize: '0.7rem' }}>{initials}</div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{inst.profile?.full_name}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-light)' }}>{inst.specialties?.slice(0,2).join(', ')}</div>
                  </div>
                </div>
                <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{instLessons.length}</div>
                <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{hours.toFixed(1)}h</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-mid)' }}>${inst.hourly_rate}/h</div>
                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--teal)', textAlign: 'right' }}>${pay.toFixed(0)}</div>
              </div>
            )
          })}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 100px',
            padding: '1rem 1.5rem', background: 'rgba(11,30,61,0.03)',
            borderTop: '2px solid rgba(11,30,61,0.08)', gap: '1rem'
          }}>
            <div style={{ fontWeight: 700, fontSize: '0.875rem' }}>Total</div>
            <div style={{ fontWeight: 700 }}>{lessons.filter(l => l.instructor_id).length}</div>
            <div style={{ fontWeight: 700 }}>—</div>
            <div />
            <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--navy)', textAlign: 'right' }}>${total.toFixed(0)}</div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function AdminWeather() {
  const [refreshed, setRefreshed] = useState(false)
  // In production, fetch from Surfline/Stormglass API
  const data = {
    location: 'North Shore', score: 8.2,
    waveHeight: '5–7 ft', period: '12s', wind: 'NE 12 mph',
    airTemp: '78°F', waterTemp: '74°F', tide: 'High 2:14 PM',
    alerts: [{ type: 'lightning', msg: 'Lightning risk 4–6 PM. Move afternoon lessons to South Beach.' }],
    forecast: [
      { day: 'Today',  height: '5-7ft', score: 8 },
      { day: 'Tue',    height: '4-5ft', score: 6 },
      { day: 'Wed',    height: '3-4ft', score: 5 },
      { day: 'Thu',    height: '6-8ft', score: 9 },
      { day: 'Fri',    height: '7-9ft', score: 9 },
      { day: 'Sat',    height: '8-10ft',score: 7 },
      { day: 'Sun',    height: '5-6ft', score: 7 },
    ]
  }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
        <div className="card">
          <div className="card-header">
            <h3 style={{ fontWeight: 600 }}>🌊 Current Conditions — {data.location}</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: 'Playfair Display, serif', color: 'var(--teal)' }}>{data.score}</span>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>/10 surf score</span>
            </div>
          </div>
          <div style={{ padding: '1.25rem', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {[
              { label: 'Wave Height', value: data.waveHeight },
              { label: 'Period', value: data.period },
              { label: 'Wind', value: data.wind },
              { label: 'Air Temp', value: data.airTemp },
              { label: 'Water Temp', value: data.waterTemp },
              { label: 'Tide', value: data.tide },
            ].map(s => (
              <div key={s.label} style={{ background: 'rgba(11,30,61,0.03)', borderRadius: 8, padding: '0.75rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)' }}>{s.value}</div>
                <div style={{ fontSize: '0.65rem', color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
          {data.alerts.map((a, i) => (
            <div key={i} style={{ margin: '0 1.25rem 1.25rem', padding: '0.75rem 1rem', background: 'rgba(212,168,75,0.08)', border: '1px solid rgba(212,168,75,0.25)', borderRadius: 8, fontSize: '0.825rem', color: '#8a6810', display: 'flex', gap: 10 }}>
              ⚠️ {a.msg}
            </div>
          ))}
        </div>

        <div className="card">
          <div className="card-header"><h3 style={{ fontWeight: 600 }}>7-Day Forecast</h3></div>
          <div style={{ padding: '1.25rem' }}>
            {data.forecast.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.5rem 0', borderBottom: i < data.forecast.length - 1 ? '1px solid rgba(11,30,61,0.05)' : 'none' }}>
                <div style={{ width: 36, fontSize: '0.8rem', fontWeight: 600, color: i === 0 ? 'var(--teal)' : 'var(--text-mid)' }}>{f.day}</div>
                <div style={{ flex: 1, height: 8, background: 'rgba(11,30,61,0.07)', borderRadius: 4 }}>
                  <div style={{ height: '100%', width: `${f.score * 10}%`, borderRadius: 4, background: f.score >= 8 ? 'var(--teal)' : f.score >= 6 ? 'var(--gold)' : 'var(--text-light)' }} />
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-mid)', width: 55, textAlign: 'right' }}>{f.height}</div>
                <div style={{ width: 24, fontSize: '0.8rem', fontWeight: 700, color: f.score >= 8 ? 'var(--teal)' : f.score >= 6 ? 'var(--gold)' : 'var(--text-light)', textAlign: 'right' }}>{f.score}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3 style={{ fontWeight: 600 }}>AI Weather Actions</h3></div>
        <div style={{ padding: '1.25rem', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.875rem' }}>
          {[
            { action: 'Move beginner lessons to Lagoon Beach', reason: 'Swell too large for beginners at North Shore Thursday–Friday', btn: 'Move Lessons', color: 'var(--coral)' },
            { action: 'Send weather alert to Saturday customers', reason: '8–10ft forecast may deter beginners. Offer reschedule proactively.', btn: 'Send SMS Blast', color: 'var(--gold)' },
            { action: 'Add 2 instructors Thursday', reason: 'Great conditions = high demand. Roster currently understaffed.', btn: 'Request Staff', color: 'var(--teal)' },
          ].map(a => (
            <div key={a.action} style={{ padding: '1rem', background: 'rgba(11,30,61,0.03)', borderRadius: 10, border: '1px solid rgba(11,30,61,0.07)' }}>
              <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 6 }}>{a.action}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-mid)', marginBottom: 12, lineHeight: 1.5 }}>{a.reason}</div>
              <button className="btn btn-sm" style={{ background: `${a.color}18`, color: a.color, border: `1px solid ${a.color}40` }}>
                {a.btn} →
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default AdminPayroll
